/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
/* tslint:disable: no-duplicate-imports */
import { Module } from '@msdyn365-commerce-modules/utilities';
import { CommerceProperty } from '@msdyn365-commerce/retail-proxy';
// import { getCheckoutState } from '@msdyn365-commerce/global-state';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { validateVatNum_CAPAsync } from '../../actions/DataActionExtension.g';
// import { getInvoiceAccountInfo_CAPAsync } from '../../actions/DataActionExtension.g';
import { ICheckoutInvoiceModuleProps, ICheckoutInvoiceViewProps } from './checkout-invoice';
import { convert_a2_to_a3 } from './common/convert_a2_a3';
import { CHECK_CODE_EVEN, CHECK_CODE_ODD, CHECK_CODE_CHARS } from './common/fiscal-code-utils';
import get from 'lodash/get';
import { getCheckoutInvoiceSchema, createTranslationContext } from '../../validation';
import { VAT_NUMBER_REGEX, FISCAL_CODE_REGEX, SDI_CODE_REGEX } from '../../validation/utils/regexes/common';

// import { CartCheckout } from '@msdyn365-commerce-modules/checkout';

interface CustomAlertProps {
    message: string;
    isVisible: boolean;
    className?: string;
}

const CustomAlert: React.FC<CustomAlertProps> = ({ message, isVisible, className = 'msc-address-form__alert' }) => {
    if (!isVisible || !message) {
        return null;
    }

    return (
        <div className={className}>
            <span>{message}</span>
        </div>
    );
};

const allFields = [
    'billing',
    'InvoiceCustAccount_CAP',
    'Type_CAP',
    'Street_CAP',
    'StreetNumber_CAP',
    'ZipCode_CAP',
    'City_CAP',
    'Country_CAP',
    'OrgName_CAP',
    'FirstName_CAP',
    'LastName_CAP',
    'VatNum_CAP',
    'FiscalCode_CAP',
    'PEC_CAP',
    'SDI_CAP'
];

const updateExtProps = async (data: Inputs, moduleProps: ICheckoutInvoiceModuleProps) => {
    const dataCompiled = [];
    Object.keys(data).forEach(key => {
        const value =
            typeof get(data, key) === 'boolean'
                ? {
                      BooleanValue: get(data, key)
                  }
                : key === 'Type_CAP'
                ? {
                      IntegerValue: Number(get(data, key))
                  }
                : { StringValue: get(data, key) };
        dataCompiled.push({
            Key: key,
            Value: value
        });
    });

    for (const str of allFields) {
        if (!Object.keys(data).includes(str)) {
            dataCompiled.push({
                Key: str,
                Value: { StringValue: '' }
            });
        }
    }

    const checkoutResult = moduleProps.data.checkout.result;

    const dataCompiledCc = [
        {
            Key: 'CustomInvoiceModuleCompiled_VEN',
            Value: {
                BooleanValue: true
            }
        }
    ];

    Array.prototype.push.apply(dataCompiledCc, dataCompiled);

    if (checkoutResult) {
        await checkoutResult.checkoutCart.updateExtensionProperties({
            newExtensionProperties: dataCompiledCc
        });
    } else {
        console.error('Error in checkout-invoice.view.tsx - updateExtProps: Cannot write invoice details on the checkout cart');
    }
};

const _capDataRender = async (moduleProps: ICheckoutInvoiceModuleProps): Promise<[Inputs | null, boolean]> => {
    //const currentCheckoutState = await getCheckoutState(moduleProps.context.actionContext);
    const currentCheckoutState = moduleProps.data.checkout.result;

    const extProps = currentCheckoutState?.checkoutCart.cart?.ExtensionProperties;
    if (!extProps) {
        return [null, false];
    }
    const extPropsToRemoveA = extProps.filter(eProp => eProp.Key === 'ExtPropsRemove_CAP');
    const invoiceModuleCompiledA = extProps.filter(eProp => eProp.Key === 'CustomInvoiceModuleCompiled_VEN');
    const extPropsToRemove = extPropsToRemoveA && extPropsToRemoveA[0]?.Value?.BooleanValue;
    const invoiceModuleCompiled = (invoiceModuleCompiledA && invoiceModuleCompiledA[0]?.Value?.BooleanValue) ?? false;

    if (extPropsToRemove !== true) {
        return [convertExtPropertiesToInput(extProps), invoiceModuleCompiled];
    }
    return [null, false];
};

const convertExtPropertiesToInput = (extProps: CommerceProperty[]): Inputs => {
    const inputs: Inputs = {} as Inputs;
    extProps.forEach(prop => {
        if (prop.Key && prop.Value) {
            const values = [prop.Value.StringValue, prop.Value.IntegerValue, prop.Value.BooleanValue].filter(
                value => value !== undefined && value !== null
            );
            const value = values.length > 0 ? values[0] : null;
            if (value !== null && value !== undefined && value !== '') {
                if (allFields.indexOf(prop.Key) > -1) {
                    inputs[prop.Key] = value;
                }
            }
        }
    });
    // Return the inputs object
    return inputs;
};

const _capDataSave = async (moduleProps: ICheckoutInvoiceModuleProps, data: Inputs) => {
    await updateExtProps(data, moduleProps);
};

type Inputs = {
    City_CAP: string;
    FiscalCode_CAP: string;
    OrgName_CAP: string;
    PEC_CAP: string;
    SDI_CAP: string;
    StreetNumber_CAP: string;
    Street_CAP: string;
    VatNum_CAP: string;
    ZipCode_CAP: string;
    billing: boolean;

    Type_CAP: number;
    Country_CAP: string;
    InvoiceCustAccount_CAP: string;
    FirstName_CAP: string;
    LastName_CAP: string;
    [key: string]: string | number | boolean;
};

interface IMaincategories {
    key: string | undefined;
    value: string | undefined;
}
const countriesWithOptionalInvoice = [
    'AUT',
    'BEL',
    'BGR',
    'HRV',
    'CYP',
    'CZE',
    'DNK',
    'EST',
    'FIN',
    'FRA',
    'DEU',
    'GRC',
    'HRV',
    'HUN',
    'IRL',
    'ITA',
    'LVA',
    'LTU',
    'LUX',
    'MLT',
    'NLD',
    'POL',
    'PRT',
    'ROU',
    'SVK',
    'SVN',
    'ESP',
    'SWE',
    'GBR'
];

const validateFiscalCode15 = (codiceFiscale: string): boolean => {
    codiceFiscale = codiceFiscale.toUpperCase();
    let val = 0;
    for (let i = 0; i < 15; i = i + 1) {
        const c = codiceFiscale.charAt(i);
        val += i % 2 !== 0 ? CHECK_CODE_EVEN.get(c) ?? 0 : CHECK_CODE_ODD.get(c) ?? 0;
    }
    val = val % 26;
    return codiceFiscale.charAt(15) === CHECK_CODE_CHARS.charAt(val);
};
const validateFiscalCode11 = (codiceFiscale: string): boolean => {
    if (!/^[0-9]{11}$/.test(codiceFiscale)) {
        return false;
    }
    if (codiceFiscale === '00000000000') {
        return false;
    }
    let val = 0;
    for (let i = 0; i < 10; i = i + 1) {
        const c = parseInt(codiceFiscale.charAt(i), 10);
        //se sono dispari
        if (i % 2 === 0) {
            val += c;
        } else {
            let evenC = c * 2;
            if (evenC > 9) {
                evenC = evenC - 9;
            }
            val += evenC;
        }
    }
    val = 10 - (val % 10);
    val = val % 10;
    return codiceFiscale.charAt(10) === val.toString();
};

const validateFiscalCode = (codiceFiscale: string): boolean => {
    if (codiceFiscale.length === 16) {
        return validateFiscalCode15(codiceFiscale);
    }
    if (codiceFiscale.length === 11) {
        return validateFiscalCode11(codiceFiscale);
    }
    return false;
};

const CheckoutInvoiceView: React.FC<ICheckoutInvoiceViewProps> = props => {
    const { CheckoutInvoice, resources, countryRegions, moduleState, moduleProps, paymentVerificationRedirection } = props;
    const [dataCompiled, setDataCompiled] = React.useState<Inputs>();
    const shippingCountry = moduleProps?.data?.checkout?.result?.shippingAddress?.ThreeLetterISORegionName;
    const [billing, setBilling] = React.useState<boolean>(false);
    const [typeSelected, setTypeSelected] = React.useState<number>(0);
    const [countrySelected, setCountrySelected] = React.useState<string>('');
    // @ts-ignore
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;

    const customerId = CheckoutInvoice.moduleProps?.context?.actionContext?.requestContext?.user?.customerAccountNumber;
    const invoiceTypeOptions = [
        { key: '1', value: resources.typePersonLabel },
        { key: '2', value: resources.typeOrgLabel }
    ];

    // Determine locale for validation
    const locale = React.useMemo(() => {
        const localeString = moduleProps?.context?.request?.locale;
        const sitePath = moduleProps?.context?.request?.sitePath;
        const currency = moduleProps?.context?.request?.channel?.Currency;
        const europe = ['/fr', '/de'];

        if (sitePath && europe.includes(sitePath)) {
            return 'eu';
        } else if (currency === 'EUR' && localeString === 'en-GB') {
            return 'eu';
        } else if (localeString) {
            return localeString.toLowerCase().replace('-', '');
        } else {
            return 'enus';
        }
    }, [moduleProps]);

    // Setup translation context for Zod validation messages
    const [translationContext, setTranslationContext] = React.useState<any>(undefined);

    // Global validation error state
    const [validationError, setValidationError] = React.useState<string>('');

    // Setup Zod schema with dynamic validation based on country and invoice type
    const invoiceSchema = React.useMemo(() => {
        return getCheckoutInvoiceSchema(translationContext, countrySelected, typeSelected);
    }, [translationContext, countrySelected, typeSelected]);

    // Load translation context
    React.useEffect(() => {
        const loadTranslations = async () => {
            try {
                const context = await createTranslationContext(locale);
                setTranslationContext(context);
            } catch (error) {
                console.warn('Failed to create translation context for invoice validation:', error);
                setTranslationContext(undefined);
            }
        };

        void loadTranslations();
    }, [locale]);

    React.useEffect(() => {
        void setData();
        // void getUserData();
    }, []);

    React.useEffect(() => {
        if (!paymentVerificationRedirection) {
            if (shippingCountry && countriesWithOptionalInvoice.indexOf(shippingCountry) === -1) {
                if (!billing) {
                    setBilling(true);
                    setValue('billing', true, { shouldDirty: true });
                    if (moduleState.status === 'ready') {
                        moduleState.onEdit && moduleState.onEdit();
                    }
                }
            } else if (typeSelected === 0) {
                setBilling(false);
                setValue('billing', false, { shouldDirty: true });
            }
        }
    }, [shippingCountry]);

    const { register, handleSubmit, errors, setValue, setError, clearErrors, reset } = useForm<Inputs>();

    React.useEffect(() => {
        if (!billing || typeSelected === 0) {
            return;
        }

        // Skip Google Autocomplete initialization if disabled
        if (!props.config.enableAutoComplete) {
            const autocompleteFormField = document.getElementsByName('Street_CAP')[0] as HTMLInputElement;
            if (autocompleteFormField) {
                // Set standard placeholder for manual input
                autocompleteFormField.setAttribute('placeholder', resources.streetLabel || 'Street');
            }
            return;
        }

        const timer = setTimeout(() => {
            const boxes = document.querySelectorAll('.pac-container');
            boxes.forEach(box => box.remove());

            const autocompleteFormField = document.getElementsByName('Street_CAP')[0] as HTMLInputElement;
            if (!autocompleteFormField) {
                return;
            }

            // @ts-ignore
            if (!window.google || !window.google.maps || !window.google.maps.places) {
                console.warn('Google Maps API not loaded');
                return;
            }

            // @ts-ignore
            const autocomplete = new window.google.maps.places.Autocomplete(autocompleteFormField, {
                types: ['address']
            });

            autocompleteFormField.setAttribute('placeholder', resources.startTypingYourAddress);

            // @ts-ignore
            window.google.maps.event.clearInstanceListeners(autocomplete);

            // @ts-ignore
            window.google.maps.event.addListener(autocomplete, 'place_changed', () => {
                const place = autocomplete.getPlace();
                if (!place || !nativeInputValueSetter) {
                    return;
                }

                nativeInputValueSetter.call(autocompleteFormField, place.name);
                const eCustom = new Event('change', { bubbles: true });
                autocompleteFormField.dispatchEvent(eCustom);

                if (place.address_components) {
                    let checkZipCode = false;
                    place.address_components.forEach((element: { types: string[]; long_name: string; short_name: string }) => {
                        switch (element.types[0]) {
                            case 'street_number':
                                setValue('StreetNumber_CAP', element.long_name, {
                                    shouldDirty: true
                                });
                                break;
                            case 'locality':
                                setValue('City_CAP', element.long_name, {
                                    shouldDirty: true
                                });
                                break;
                            case 'country':
                                const countryValue = convert_a2_to_a3[element.short_name as keyof typeof convert_a2_to_a3];
                                setValue('Country_CAP', countryValue, { shouldDirty: true });
                                setCountrySelected(countryValue);
                                break;
                            case 'postal_code':
                                setValue('ZipCode_CAP', element.long_name, {
                                    shouldDirty: true
                                });
                                checkZipCode = true;
                                break;
                            default:
                                break;
                        }
                    });
                    if (!checkZipCode) {
                        setValue('ZipCode_CAP', '', { shouldDirty: true });
                    }
                }
                const streetInput = autocompleteFormField;
                if (streetInput.value.includes(',')) {
                    streetInput.value = streetInput.value.split(',')[0];
                }
            });
        }, 100);

        return () => {
            clearTimeout(timer);
        };
    }, [billing, typeSelected, resources, setValue, setCountrySelected, nativeInputValueSetter, props.config.enableAutoComplete]);

    // Setup field validation with real-time feedback
    React.useEffect(() => {
        if (!invoiceSchema || !billing || typeSelected === 0) {
            return;
        }

        // Add delay to ensure DOM is ready after Google Maps autocomplete initialization
        const setupTimer = setTimeout(() => {
            const cleanupFunctions: (() => void)[] = [];

            const validateSingleField = (fieldName: string, value: string) => {
                // Basic validation patterns for common fields
                if (!value || value.trim() === '') {
                    return { isValid: false, message: resources.genericFieldError || 'This field is required' };
                }

                // Field-specific validation
                switch (fieldName) {
                    case 'Street_CAP':
                    case 'StreetNumber_CAP':
                    case 'City_CAP':
                        if (value.length < 1) {
                            return { isValid: false, message: resources.genericFieldError || 'This field is required' };
                        }
                        break;

                    case 'ZipCode_CAP':
                        if (value.length < 3 || value.length > 10) {
                            return { isValid: false, message: resources.genericFieldError || 'Invalid postal code' };
                        }
                        break;

                    case 'FirstName_CAP':
                    case 'LastName_CAP':
                        if (value.length < 2 || value.length > 50) {
                            return { isValid: false, message: 'Name must be between 2 and 50 characters' };
                        }
                        if (!/^[a-zA-ZÀ-ÿ\s-']+$/.test(value)) {
                            return { isValid: false, message: 'Name can only contain letters, spaces, hyphens, and apostrophes' };
                        }
                        break;

                    case 'OrgName_CAP':
                        if (value.length < 2) {
                            return { isValid: false, message: 'Organization name must be at least 2 characters' };
                        }
                        break;

                    case 'FiscalCode_CAP':
                        if (countrySelected === 'ITA') {
                            if (!FISCAL_CODE_REGEX.test(value)) {
                                return { isValid: false, message: resources.fiscalCodeError || 'Invalid fiscal code format' };
                            }
                        }
                        break;

                    case 'VatNum_CAP':
                        // VAT pattern check - alphanumeric (international format)
                        if (value && !VAT_NUMBER_REGEX.test(value)) {
                            return {
                                isValid: false,
                                message:
                                    resources.vatNumberError || 'VAT number must contain only letters and numbers (maximum 15 characters)'
                            };
                        }
                        break;

                    case 'PEC_CAP':
                        // Email validation for PEC
                        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                            return { isValid: false, message: 'Invalid email format' };
                        }
                        break;

                    case 'SDI_CAP':
                        if (countrySelected === 'ITA' && typeSelected === 2) {
                            if (value.length !== 7 || !SDI_CODE_REGEX.test(value)) {
                                return { isValid: false, message: 'SDI code must be 7 alphanumeric characters' };
                            }
                        }
                        break;

                    default:
                        break;
                }

                return { isValid: true, message: '' };
            };

            const fieldMappings = [
                { name: 'Street_CAP', required: true },
                { name: 'StreetNumber_CAP', required: true },
                { name: 'ZipCode_CAP', required: true },
                { name: 'City_CAP', required: true },
                { name: 'FirstName_CAP', required: typeSelected === 1 },
                { name: 'LastName_CAP', required: typeSelected === 1 },
                { name: 'OrgName_CAP', required: typeSelected === 2 },
                { name: 'VatNum_CAP', required: false },
                { name: 'FiscalCode_CAP', required: countrySelected === 'ITA' },
                { name: 'PEC_CAP', required: countrySelected === 'ITA' && typeSelected === 2 },
                { name: 'SDI_CAP', required: countrySelected === 'ITA' && typeSelected === 2 }
            ];

            fieldMappings.forEach(mapping => {
                const field = document.getElementById(mapping.name) as HTMLInputElement;
                if (!field) {
                    return;
                }

                const clearErrorHandler = () => {
                    const container = field.closest('.msc-address-form__item') as HTMLElement;
                    if (container) {
                        container.classList.remove('address-form__item-invalid');
                    }
                    // Properly clear react-hook-form error
                    clearErrors(mapping.name);
                };

                const validateField = () => {
                    const value = field.value.trim();
                    const container = field.closest('.msc-address-form__item') as HTMLElement;

                    // Clear previous error
                    if (container) {
                        container.classList.remove('address-form__item-invalid');
                    }

                    // Skip validation if field is empty and not required
                    if (!value && !mapping.required) {
                        clearErrors(mapping.name);
                        return true;
                    }

                    // Validate with pattern
                    const validation = validateSingleField(mapping.name, value);
                    if (!validation.isValid) {
                        if (container) {
                            container.classList.add('address-form__item-invalid');
                        }
                        setError(mapping.name, {
                            message: validation.message,
                            shouldFocus: false
                        });
                        return false;
                    }

                    clearErrors(mapping.name);
                    return true;
                };

                // Attach event listeners
                field.addEventListener('input', clearErrorHandler);
                field.addEventListener('blur', validateField);

                cleanupFunctions.push(() => {
                    field.removeEventListener('input', clearErrorHandler);
                    field.removeEventListener('blur', validateField);
                });
            });

            // Validation for select fields
            const selectMappings = [{ name: 'Country_CAP', required: true }];

            selectMappings.forEach(mapping => {
                const select = document.getElementById(mapping.name) as HTMLSelectElement;
                if (!select) {
                    return;
                }

                const clearErrorHandler = () => {
                    const container = select.closest('.msc-address-form__item') as HTMLElement;
                    if (container) {
                        container.classList.remove('address-form__item-invalid');
                    }
                    clearErrors(mapping.name);
                };

                const validateSelect = () => {
                    const value = select.value;
                    const container = select.closest('.msc-address-form__item') as HTMLElement;

                    if (container) {
                        container.classList.remove('address-form__item-invalid');
                    }

                    if (!value || value === '0' || value === '') {
                        if (mapping.required) {
                            if (container) {
                                container.classList.add('address-form__item-invalid');
                            }
                            setError(mapping.name, {
                                message: resources.genericFieldError || 'This field is required',
                                shouldFocus: false
                            });
                            return false;
                        }
                    }

                    clearErrors(mapping.name);
                    return true;
                };

                select.addEventListener('change', clearErrorHandler);
                select.addEventListener('blur', validateSelect);

                cleanupFunctions.push(() => {
                    select.removeEventListener('change', clearErrorHandler);
                    select.removeEventListener('blur', validateSelect);
                });
            });

            return () => {
                cleanupFunctions.forEach(cleanup => cleanup());
            };
        }, 200); // Wait 200ms after autocomplete setup

        // Cleanup timer on unmount
        return () => {
            clearTimeout(setupTimer);
        };
    }, [invoiceSchema, billing, typeSelected, countrySelected, resources, setError, clearErrors]);

    const _renderInput = (name: string, label: string, required: boolean, errorMessage: string, pattern?: RegExp): JSX.Element => {
        return (
            <div className={`msc-address-form__item msc-address-form__item--${name}`} aria-invalid={get(errors, name) ? 'true' : 'false'}>
                <label key={`${name}-label-key`} htmlFor={name} className={'msc-address-form__label'}>
                    {label} {required && '*'}
                </label>
                <input
                    key={`${name}-input-key`}
                    id={name}
                    name={name}
                    ref={register({ required: required, pattern: pattern })}
                    className={'msc-address-form__input msc-address-form__input-text'}
                />
                {get(errors, name) && <span className={'error-message'}>{errorMessage}</span>}
            </div>
        );
    };

    const _renderTypeSelect = (name: string, label: string, options: IMaincategories[], required: boolean): JSX.Element => {
        return (
            <div className={'msc-address-form__item'} aria-invalid={get(errors, name) ? 'true' : 'false'}>
                <label key={`${name}-label-key`} htmlFor={name} className={'msc-address-form__label'}>
                    {label} {required && '*'}
                </label>
                <select
                    id={name}
                    value={typeSelected}
                    key={`${name}-select-key`}
                    name={name}
                    className={'msc-address-form__dropdown'}
                    ref={register({ required: required })}
                    onChange={e => {
                        setValue('Type_CAP', e.target.value, {
                            shouldDirty: true
                        });
                        setTypeSelected(parseInt(e.target.value, 10));
                    }}
                >
                    <option key={`${name}-option-key`} aria-selected='true' value={0}>
                        {resources.selectOption}
                    </option>
                    {options.map(option => {
                        return (
                            <option key={option.key} aria-selected='false' value={option.key}>
                                {option.value}
                            </option>
                        );
                    })}
                    ;
                </select>
                {get(errors, name) && <span className={'error-message'}>{resources.genericFieldError}</span>}
            </div>
        );
    };

    const _renderCountrySelect = (name: string, label: string, options: IMaincategories[], required: boolean): JSX.Element => {
        return (
            <div className={'msc-address-form__item'} aria-invalid={get(errors, name) ? 'true' : 'false'}>
                <label key={`${name}-label-key`} htmlFor={name} className={'msc-address-form__label'}>
                    {label} {required && '*'}
                </label>
                <select
                    id={name}
                    key={`${name}-select-key`}
                    name={name}
                    className={'msc-address-form__dropdown'}
                    ref={register({ required: required })}
                    defaultValue={countrySelected}
                    onChange={e => {
                        const newValue = e.target.value;
                        setCountrySelected(newValue);
                        setValue('Country_CAP', newValue, {
                            shouldDirty: true
                        });
                    }}
                >
                    <option key={`${name}-option-key`} aria-selected='true' value=''>
                        {resources.selectOption}
                    </option>
                    {options.map(option => {
                        return (
                            <option key={option.key} aria-selected='false' value={option.key}>
                                {option.value}
                            </option>
                        );
                    })}
                </select>
                {get(errors, name) && <span className={'error-message'}>{resources.genericFieldError}</span>}
            </div>
        );
    };

    const onSubmit = async (data: Inputs) => {
        // Clear previous validation error
        setValidationError('');

        // Validate with Zod schema first (if available and billing is active)
        if (invoiceSchema && billing && typeSelected > 0) {
            try {
                invoiceSchema.parse(data);
            } catch (zodError) {
                // Handle Zod validation errors
                const error = zodError as { errors?: { path: (string | number)[]; message: string }[] };
                if (error?.errors) {
                    // Set a global error message
                    setValidationError(resources.genericFieldError || 'Please correct the errors in the form before submitting');

                    // Mark each field with error
                    error.errors.forEach(err => {
                        const fieldName = err.path[0] as string;
                        setError(fieldName, {
                            message: err.message || resources.genericFieldError,
                            shouldFocus: true
                        });
                    });
                }
                return; // Stop submission if Zod validation fails
            }
        }

        if (data.VatNum_CAP && data.VatNum_CAP !== '') {
            void validateVatNum_CAPAsync({ callerContext: moduleProps.context?.actionContext }, data.VatNum_CAP, data.Country_CAP).then(
                response => {
                    if (response.Status === true) {
                        if (data.Country_CAP === 'ITA' && data.FiscalCode_CAP && data.FiscalCode_CAP !== '') {
                            if (validateFiscalCode(data.FiscalCode_CAP)) {
                                setDataCompiled(data);
                                void _capDataSave(moduleProps, data);
                                moduleState.onReady();
                                reset(data);
                            } else {
                                setError('FiscalCode_CAP', {
                                    message: 'Fiscal Code Num not valid',
                                    shouldFocus: true
                                });
                            }
                        } else {
                            setDataCompiled(data);
                            void _capDataSave(moduleProps, data);
                            moduleState.onReady();
                            reset(data);
                        }
                    } else {
                        setError('VatNum_CAP', {
                            message: 'Vat Num not valid',
                            shouldFocus: true
                        });
                    }
                }
            );
        } else {
            if (data.Country_CAP === 'ITA' && data.FiscalCode_CAP && data.FiscalCode_CAP !== '') {
                if (validateFiscalCode(data.FiscalCode_CAP)) {
                    setDataCompiled(data);
                    void _capDataSave(moduleProps, data);
                    moduleState.onReady();
                    reset(data);
                } else {
                    setError('FiscalCode_CAP', {
                        message: 'Fiscal Code Num not valid',
                        shouldFocus: true
                    });
                }
            } else {
                setDataCompiled(data);
                void _capDataSave(moduleProps, data);
                moduleState.onReady();
                reset(data);
            }
        }
    };

    const _resetForm = () => {
        if (dataCompiled?.billing && !billing) {
            setBilling(true);
            setCountrySelected(dataCompiled.Country_CAP);
            setTypeSelected(dataCompiled.Type_CAP);
        } else if (dataCompiled && billing) {
            setCountrySelected(dataCompiled.Country_CAP);
            setTypeSelected(dataCompiled.Type_CAP);
        }
        moduleState.onReady();
    };

    const setData = async () => {
        const [dataLoadedFromExtProps, pageRefreshedDataSaved] = await _capDataRender(moduleProps);
        if (dataLoadedFromExtProps && pageRefreshedDataSaved && dataLoadedFromExtProps.billing !== undefined) {
            setDataCompiled(dataLoadedFromExtProps);
            setBilling(dataLoadedFromExtProps.billing);
            dataLoadedFromExtProps.Type_CAP && setTypeSelected(dataLoadedFromExtProps.Type_CAP);
            dataLoadedFromExtProps.Country_CAP && setCountrySelected(dataLoadedFromExtProps.Country_CAP);
            moduleState.onReady();
            reset(dataLoadedFromExtProps);
        }
    };

    // const getUserData = async () => {
    //     if (customerId) {
    //         await getInvoiceAccountInfo_CAPAsync({ callerContext: CheckoutInvoice.moduleProps.context?.actionContext }, customerId).then(
    //             result => {
    //                 // console.log('getInvoiceAccountInfo_CAPAsync');
    //                 // console.log(result);
    //             }
    //         );
    //     }
    // };

    if (moduleState.status === 'ready') {
        return (
            <Module {...CheckoutInvoice}>
                <div className='msc-address-detail'>
                    {dataCompiled &&
                        Object.keys(dataCompiled).map((keyName, i) => (
                            <span key={i} className={`msc-address-detail__item ${keyName}`}>
                                {get(dataCompiled, keyName)}
                                <br />
                            </span>
                        ))}
                </div>
            </Module>
        );
    } else {
        return (
            <Module {...CheckoutInvoice}>
                <div className={'msc-address-form__item inputBox privacy_field_ctn'} aria-invalid={errors.billing ? 'true' : 'false'}>
                    <label className={shippingCountry && countriesWithOptionalInvoice.indexOf(shippingCountry) === -1 ? 'hide' : 'show'}>
                        <input
                            type={'checkbox'}
                            disabled={!!(shippingCountry && countriesWithOptionalInvoice.indexOf(shippingCountry) === -1)}
                            name={'billing'}
                            checked={billing}
                            onChange={e => setBilling(e.target.checked)}
                            ref={register({ required: false })}
                        />
                        <div>{props.resources.billingLabel}</div>
                    </label>
                    {shippingCountry && countriesWithOptionalInvoice.indexOf(shippingCountry) === -1 && (
                        <p>
                            <strong>{props.config.billingLabel}</strong>
                        </p>
                    )}
                </div>
                {billing && (
                    <>
                        <CustomAlert message={validationError} isVisible={!!validationError} className='msc-address-form__alert' />
                        {props.config.invoiceNotIncludedLabel && (
                            <div className='ms-checkout__info-message'>
                                <p className='ms-checkout__info-message-body'>{props.config.invoiceNotIncludedLabel}</p>
                            </div>
                        )}
                        {customerId && (
                            <div className={'msc-address-form__item msc-address-form__item--InvoiceCustAccount_CAP'}>
                                <input
                                    aria-label='Customer Id'
                                    name={'InvoiceCustAccount_CAP'}
                                    value={customerId}
                                    ref={register()}
                                    className={'msc-address-form__input msc-address-form__input-text'}
                                />
                            </div>
                        )}
                        {_renderTypeSelect('Type_CAP', `${resources.typeLabel}`, invoiceTypeOptions, true)}
                        {typeSelected !== 0 &&
                            _renderInput('Street_CAP', `${resources.streetLabel}`, true, `${resources.genericFieldError}`)}
                        {typeSelected !== 0 &&
                            _renderInput('StreetNumber_CAP', `${resources.streetNumberLabel}`, true, `${resources.genericFieldError}`)}
                        {typeSelected !== 0 &&
                            _renderInput('ZipCode_CAP', `${resources.zipCodeLabel}`, true, `${resources.genericFieldError}`)}
                        {typeSelected !== 0 && _renderInput('City_CAP', `${resources.cityLabel}`, true, `${resources.genericFieldError}`)}
                        {typeSelected !== 0 && _renderCountrySelect('Country_CAP', `${resources.countryLabel}`, countryRegions, true)}
                        {typeSelected === 2 &&
                            _renderInput('OrgName_CAP', `${resources.orgNameLabel}`, true, `${resources.genericFieldError}`)}
                        {typeSelected === 1 &&
                            _renderInput('FirstName_CAP', `${resources.firstNameLabel}`, true, `${resources.genericFieldError}`)}
                        {typeSelected === 1 &&
                            _renderInput('LastName_CAP', `${resources.lastNameLabel}`, true, `${resources.genericFieldError}`)}
                        {typeSelected === 2 &&
                            countriesWithOptionalInvoice.indexOf(countrySelected) > -1 &&
                            _renderInput('VatNum_CAP', `${resources.vatNumLabel}`, true, `${resources.vatNumberError}`)}
                        {typeSelected !== 0 &&
                            countrySelected === 'ITA' &&
                            _renderInput(
                                'FiscalCode_CAP',
                                `${resources.fiscalCodeLabel}`,
                                true,
                                `${resources.fiscalCodeError}`,
                                FISCAL_CODE_REGEX
                            )}
                        {typeSelected !== 0 &&
                            countrySelected === 'ITA' &&
                            typeSelected === 2 &&
                            _renderInput('PEC_CAP', `${resources.pecLabel}`, true, `${resources.genericFieldError}`)}
                        {typeSelected !== 0 &&
                            countrySelected === 'ITA' &&
                            typeSelected === 2 &&
                            _renderInput('SDI_CAP', `${resources.sdiLabel}`, true, `${resources.genericFieldError}`)}
                    </>
                )}
                <div className='ms-checkout__guided-card-footer'>
                    <button
                        onClick={handleSubmit(onSubmit)}
                        type='button'
                        title='Save &amp; continue'
                        className={`ms-checkout__guided-card-btn-save msc-btn btn-primary ${
                            billing && typeSelected === 0 ? 'disable-save-billing' : ''
                        }`}
                        disabled={billing && typeSelected === 0}
                    >
                        {resources.saveContinueBtnLabel}
                    </button>
                    <button onClick={_resetForm} title='Cancel' className='ms-checkout__guided-card-btn-cancel msc-btn btn-secondary'>
                        {resources.cancelBtnLabel}
                    </button>
                </div>
            </Module>
        );
    }
};
export default CheckoutInvoiceView;
